import { useFormik } from 'formik';
import {
    FC, MouseEventHandler, useEffect, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { createFeedThunk, updateFeedThunk } from '../../../../actions/feeds';
import { getFeedPropertyDefinitions } from '../../../../api/feeds';
import { Breadcrumbs } from '../../../../components/Breadcrumbs';
import { Checkbox } from '../../../../components/Checkbox';
import { Icon } from '../../../../components/Icon';
import { feedsBreadcrumbs } from '../../../../constants/BreadCrumbs/feedsBreadcrumbs';
import { TypesButton } from '../../../../constants/ButtonTypes';
import { routes } from '../../../../constants/RouterPath';
import { SizesButton } from '../../../../constants/SizeButton';
import { selectCategories } from '../../../../selectors/catalog';
import { useAppDispatch, useTypeSelector } from '../../../../store';
import { Nullable } from '../../../../types/common';
import {
    FeedDefinitionPropertyCollection,
    FeedFormState,
    FeedPropertyDefinitions, Feeds,
} from '../../../../types/Endpoints/feeds';
import { TypesIcon } from '../../../../types/TypesIcon';
import { FilterGroup } from '../../../Sales/Filters/FilterGroup';
import { FilterGroupType } from '../../../Sales/Filters/types';
import { AttributesDrawer } from './components/AttributesDrawer';
import { MainFormControls } from './components/MainFormControls';
import { ErrorWrapper } from './styles';
import * as S from './styles';

interface Props {
  type: 'create' | 'edit';
  data?: Nullable<Feeds[number]>;
}

const hiddenFilters = ['Категория'];

const formSchema = yup.object().shape({
    title: yup.string().required('Поле является обязательным'),
    categories: yup.array().of(yup.number()).min(1, 'Должно быть выбрано не менее одной категории'),
});

const initialFormState = {
    title: '',
    categories: [],
    properties: [],
    filters: {
        condition: 'AND',
        filters: [],
    } as FilterGroupType,
};

export const Feed: FC<Props> = ({ type, data }) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const categories = useTypeSelector(selectCategories);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [definitions, setDefinitions] = useState<FeedPropertyDefinitions>([]);
    const [filters, setFilters] = useState<FilterGroupType>(initialFormState.filters);

    const onSubmit = (values: FeedFormState) => {
        const body = {
            ...values,
            filters: values.filters.filters.length ? values.filters : null,
            categories: values.categories.map(id => ({ id })),
            properties: values.properties.filter(({ key }) => key !== 'custom_labels'),
        };

        dispatch(type === 'create' ? createFeedThunk(body) : updateFeedThunk({
            ...body,
            ...({ id: data?.id || -1 }),
        }));
    };

    const form = useFormik<FeedFormState>({
        onSubmit,
        initialValues: initialFormState,
        validationSchema: formSchema,
    });

    const handleFiltersChange = (updatedFilters: FilterGroupType) => {
        setFilters(updatedFilters);
        form.setFieldValue('filters', updatedFilters);
    };

    const handleCheck: MouseEventHandler<HTMLInputElement> = e => {
        const id = +e.currentTarget.name;
        let initCategories: number[];

        if (form.values.categories.includes(id)) {
            initCategories = form.values.categories.filter(item => item !== id);
        } else {
            initCategories = [...form.values.categories, id];
        }

        form.setFieldValue('categories', initCategories);
    };

    const openDrawer = async () => {
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    const handleDrawerSave = (properties: FeedDefinitionPropertyCollection) => {
        form.setFieldValue('properties', properties);
        setIsDrawerOpen(false);
    };

    const handleBack = () => {
        history.push(routes.feeds);
    };

    useEffect(() => {
        (async () => {
            try {
                const response = await getFeedPropertyDefinitions();
                const props = response.filter(item => item.required).map(({ required, key }) => ({ key, isEnabled: required, value: null }));
                setDefinitions(response);
                await form.setFieldValue('properties', props);
            } catch {
                console.error('Error while getting data using getFeedPropertyDefinitions');
            }
        })();
    }, []);

    useEffect(() => {
        if (!data) return;

        if (data.filters) {
            setFilters(data.filters);
        }

        form.setValues({
            title: data.title,
            categories: data.categories.map(({ id }) => id),
            filters: data.filters ?? initialFormState.filters,
            properties: data.properties,
        });
    }, [data]);

    return (
        <S.Wrapper>
            <S.HeadingWrapper>
                <S.StyledPageTitle>Фиды</S.StyledPageTitle>
                <Breadcrumbs breadcrumbs={feedsBreadcrumbs(type === 'edit' ? 'Редактировать фид' : 'Добавить фид')} />
            </S.HeadingWrapper>
            <S.StyledInput
                name="title"
                onChange={form.handleChange}
                value={form.values.title}
                placeholder="Название"
                typeInput="text"
                maxLength={255}
                title="Название"
                isTouched={form.touched.title}
                error={form.errors.title}
                count={form.submitCount}
            />
            <S.SectionContainer>
                <S.Subtitle>Категория</S.Subtitle>
                <S.CategoriesWrapper>
                    {categories && categories.map(({ id, name }) => (
                        <Checkbox
                            key={id}
                            name={id.toString()}
                            title={name}
                            checked={form.values.categories.includes(id)}
                            onChange={handleCheck}
                        />
                    ))}
                    {form.submitCount > 0 && form.errors.categories && <ErrorWrapper>{form.errors.categories}</ErrorWrapper>}
                </S.CategoriesWrapper>
            </S.SectionContainer>
            <S.SectionContainer>
                <S.Subtitle>Связанные товары</S.Subtitle>
                <FilterGroup group={filters} hiddenFilters={hiddenFilters} onChange={handleFiltersChange} />
            </S.SectionContainer>
            <S.SettingsButton
                typeButton={TypesButton.SECONDARY}
                size={SizesButton.M}
                onClick={openDrawer}
            >
                <Icon
                    type={TypesIcon.EDIT}
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                />
                Настройка атрибутов
            </S.SettingsButton>

            {isDrawerOpen && (
                <AttributesDrawer
                    isOpen={isDrawerOpen}
                    onClose={closeDrawer}
                    onSave={handleDrawerSave}
                    properties={form.values.properties}
                    definitions={definitions}
                />
            )}
            <MainFormControls onCancel={handleBack} onSave={form.submitForm} />
        </S.Wrapper>
    );
};
